import { Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { formatImageIntensity } from '~/domain/dicom/intensityUnits';
import {
    IntensityRegion,
    regionAreaMm2,
    regionCenterMm,
} from '~/domain/intensity/intensityRegion';
import { Image3D } from '~/domain/patient/image3d';
import { formatRegionSize } from '~/utils/measurementFormat';
import { SUMMARY_STAT_LABEL_IDS } from '~/utils/summaryStatLabels';
import { formatMm2AsCm2 } from '~/utils/units';
import AnalysisHeader from './AnalysisHeader';

/**
 * The selected region's size (a circle's diameter, a rectangle's sides), then
 * mean, SD and area over min, max and the pixel count. Every intensity
 * carries its image's unit; one unit fits the SD too, as the display
 * conversion is a plain scale factor. Without the image there is no unit or
 * scale, so the figures are not shown; nor while a dose is shown, where they
 * could pass for a dose statistic (SR-WOR-H-17-5).
 */
export default function IntensityRegionDetail({
    region,
    image,
    doseShown,
    goTo,
}: {
    region: IntensityRegion;
    image: Image3D | undefined;
    doseShown: boolean;
    goTo: (id: string) => void;
}) {
    const intl = useIntl();
    const { stats } = region;
    const intensity = (value: number) => formatImageIntensity(value, image);
    const figures = stats && [
        [SUMMARY_STAT_LABEL_IDS.mean, intensity(stats.mean)],
        [SUMMARY_STAT_LABEL_IDS.std, intensity(stats.std)],
        ['measure.stat.area', `${formatMm2AsCm2(regionAreaMm2(region))} cm²`],
        [SUMMARY_STAT_LABEL_IDS.min, intensity(stats.min)],
        [SUMMARY_STAT_LABEL_IDS.max, intensity(stats.max)],
        // Plain digits: some locales' grouping separator reads as a decimal.
        ['measure.stat.pixels', String(stats.count)],
    ];
    return (
        <>
            <AnalysisHeader
                id={region.id}
                plane={region.plane}
                anchorMm={regionCenterMm(region)}
                titleId={`measure.mode.${region.shape}`}
                readout={formatRegionSize(region)}
                goTo={goTo}
            />
            {!doseShown && image && figures ? (
                <Box
                    data-testid="region-analysis-figures"
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                        columnGap: 1,
                        rowGap: 0.75,
                    }}
                >
                    {figures.map(([id, value]) => (
                        <Box key={id}>
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    color: 'text.secondary',
                                    fontSize: 11,
                                }}
                            >
                                {intl.formatMessage({ id })}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {value}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            ) : (
                <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'text.secondary' }}
                >
                    {intl.formatMessage({
                        id: doseShown
                            ? 'intensityRegion.figuresHiddenWhileDoseShown'
                            : image
                              ? 'intensityRegion.noStats'
                              : 'intensityRegion.noImage',
                    })}
                </Typography>
            )}
        </>
    );
}
